/**
 * Planning recurring visits — the same rule as the backend (oms-be api/Recurrence.java), so the
 * order page's "Future orders" preview shows exactly the dates that will be created:
 * a series keeps its day of the month (the anchor; the 31st becomes the last day of shorter
 * months), weekend moves only affect that one visit, and the next visit is planned from the
 * nominal (intended) date. Dates are plain calendar dates ("yyyy-mm-dd").
 */
export type Ymd = { y: number; m: number; d: number }; // m: 1–12

const pad = (n: number) => String(n).padStart(2, "0");
export const ymdToString = ({ y, m, d }: Ymd) => `${y}-${pad(m)}-${pad(d)}`;
export const ymdFromString = (s: string): Ymd => {
    const [y, m, d] = s.split("-").map(Number);
    return { y, m, d };
};
/** Calendar date of a Date coming from the API (UTC midnight for a plain date). */
export const ymdFromDate = (date: Date): Ymd => ({ y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() });

const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const weekday = ({ y, m, d }: Ymd) => new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
const addDays = ({ y, m, d }: Ymd, days: number): Ymd => {
    const t = new Date(Date.UTC(y, m - 1, d + days));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};

export const nextNominal = (nominal: Ymd, months: number, anchorDay: number): Ymd => {
    const index = nominal.y * 12 + (nominal.m - 1) + months;
    const y = Math.floor(index / 12);
    const m = (index % 12) + 1;
    return { y, m, d: Math.min(anchorDay, daysInMonth(y, m)) };
};

/** Saturday → the Friday before, Sunday → the Monday after. */
export const avoidWeekend = (date: Ymd): Ymd => {
    const wd = weekday(date);
    return wd === 6 ? addDays(date, -1) : wd === 0 ? addDays(date, 1) : date;
};

type Extra = { recurringNominalDate?: unknown; recurringAnchorDay?: unknown } | undefined;

/** The series' intended date for this order and its anchor day (falls back to the scheduled date). */
export const seriesStart = (scheduled: Ymd, extra: Extra): { nominal: Ymd; anchorDay: number } => {
    const nominal = typeof extra?.recurringNominalDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(extra.recurringNominalDate)
        ? ymdFromString(extra.recurringNominalDate) : scheduled;
    const anchor = Number(extra?.recurringAnchorDay);
    return { nominal, anchorDay: anchor >= 1 && anchor <= 31 ? anchor : nominal.d };
};

/** The planned date of visit number `i` after this one (i ≥ 1), before any one-off override. */
export const plannedVisit = (scheduled: Ymd, extra: Extra, intervalMonths: number, i: number): Ymd => {
    const { nominal, anchorDay } = seriesStart(scheduled, extra);
    return avoidWeekend(nextNominal(nominal, intervalMonths * i, anchorDay));
};
